import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {RestUrlsConfig} from '@signature-it/ngx-generic/config';
import {CommonService, PageNotificationService, CookieDisclaimerService} from './public-api';

declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class FeaturesService extends CommonService {

    protected newsUrl = `${this.restUrlsConf.appURL}/public/news.json`;
    protected commonShearchsUrl = `${this.restUrlsConf.appURL}/public/common_searches.json`;
    protected cuUrl = `${this.restUrlsConf.appURL}/public/contact_us_mail`;
    protected pickupUrl = `${this.restUrlsConf.appURL}/public/branches.json`;
    protected nrlUrl = `${this.restUrlsConf.appURL}/public/newsletter_register`;
    protected newsLetterTListlUrl = `${this.restUrlsConf.appURL}/public/newsletter_type_list`;
    protected newsLetterUnsubscribeDataUrl = `${this.restUrlsConf.appURL}/public/newsletter_unsubscribe_data`;
    protected subUnsubscribeUrl = `${this.restUrlsConf.appURL}/public/sub_unsubscribe`;
    protected addUserToSubscribeNewLetter = `${this.restUrlsConf.appURL}/public/add_user_to_subscribe_newsletter`;
    protected resourcesUrl = `${this.restUrlsConf.appURL}/public/get_external_resources`;
    protected genericEntityGenericScriptsAndStylesUrl = `${this.restUrlsConf.appURL}/public/entity/generic_script_ans_style`;
    protected countryStateUrl = `${this.restUrlsConf.appURL}/public/country_state.json`;
    cookieDisclaimer$;

    constructor(protected http: HttpClient,
                protected restUrlsConf: RestUrlsConfig,
                protected notifySvc: PageNotificationService,
                protected cookieDisclaimerSvc: CookieDisclaimerService) {
        super(http, restUrlsConf, notifySvc);

        this.cookieDisclaimer$ = this.cookieDisclaimerSvc.cookieDisclaimer$;
    }

    getNews(): Observable<any> {
        return this.http.get(this.newsUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getNewByID(newID: number): Observable<any> {
        const newByIDUrl = `${this.restUrlsConf.appURL}/public/new/${newID}.json`;
        return this.http.get(newByIDUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getBranches(): Observable<any> {
        return this.http.get(this.pickupUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getBrancheByID(branchID: number): Observable<any> {
        const branchByIDUrl = `${this.restUrlsConf.appURL}/public/branch/${branchID}.json`;
        return this.http.get(branchByIDUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getCommonSearches(langCode = '', types = []): Observable<any> {
        return this.http.get(`${this.commonShearchsUrl}?lang=${langCode}&types=${types.toString()}`)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    sendContactUsMail(data): Observable<any> {
        return this.http.post(this.cuUrl, JSON.stringify(data))
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    newsletterRegister(data): Observable<any> {
        return this.http.post(this.nrlUrl, JSON.stringify(data))
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getNewsletterTypeList(): Observable<any> {
        return this.http.get(this.newsLetterTListlUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getDataNewsLetterUnsubscribe(token = ''): Observable<any> {
        const newsLetterUnsubscribeDataUrl = `${this.newsLetterUnsubscribeDataUrl}` + (token != '' ? '?token=' + token : '');
        return this.http.get(newsLetterUnsubscribeDataUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }


    subUnsubscribe(form: any): Observable<any> {
        return this.http.post(this.subUnsubscribeUrl, JSON.stringify(form))
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    addUserToSubscribe(form: any): Observable<any> {
        return this.http.post(this.addUserToSubscribeNewLetter, JSON.stringify(form))
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }


    getExternalResources(): Observable<any> {
        return this.http.get(this.resourcesUrl)
            .pipe(map((res) => {
                _.each(res, (resourceList, location) => {
                    res[location] = this.cookieDisclaimerSvc.filterResourcesByCookie(resourceList);
                });
                return res;
            }), catchError((error: any) => throwError(error)));
    }

    getEntityGenericScriptsAndStyles(): Observable<any> {
        return this.http.get(this.genericEntityGenericScriptsAndStylesUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getPickup(): Observable<any> {
        return this.http.get(this.pickupUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getGroupBanner(groupID: number): Observable<any> {
        const groupBannerUrl = `${this.restUrlsConf.appURL}/public/group/banner/${groupID}.json`;
        return this.http.get(groupBannerUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getCatalogueSortOptions(groupId: number) {
        const catalogueSortOptions = `${this.restUrlsConf.appURL}/public/catalogue_sort_options/${groupId}.json`;
        return this.http.get(catalogueSortOptions)
            .pipe(tap((res: []) => res, (error: any) => throwError(error)));
    }

    getRedirectUrl(currentPath): Observable<string> {
        const redirectApiUrl = `${this.restUrlsConf.appURL}/public/getRedirectUrl?url=${currentPath}`;
        return this.http.get<string>(redirectApiUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getCountryState(): Observable<any> {
        return this.http.get(this.countryStateUrl)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }

    getResponseByApiUrl(apiUrl): Observable<any> {
        return this.http.get(`${this.restUrlsConf.appURL}/${apiUrl}`)
            .pipe(map((res) => res), catchError((error: any) => throwError(error)));
    }
}
